import { useEffect, useState } from "react";
import { Menu, ShoppingBag, X } from "lucide-react";
import { Link, useLocation } from "wouter";
import { ASSETS, CTA_TEXT, NAV_LINKS } from "@/lib/brand";
import { openWhatsApp } from "@/components/WhatsAppLeadModal";
import { trpc } from "@/lib/trpc";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [location] = useLocation();

  // Configuração dinâmica da loja Tray (rota pública — sem autenticação)
  const { data: storeSettings } = trpc.siteConfig.storeSettings.useQuery(undefined, {
    staleTime: 5 * 60 * 1000, // cache 5 min
  });
  const settingsMap = Object.fromEntries(
    (storeSettings ?? []).map((s) => [s.key, s.value ?? ""])
  );
  const trayUrl = settingsMap["tray_store_url"]?.trim();
  const trayLabel = settingsMap["tray_store_label"]?.trim() || "Loja";
  const trayShowNav = settingsMap["tray_show_nav_link"] === "1";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 bg-charcoal transition-shadow duration-300 ${
        scrolled ? "shadow-[0_1px_0_0_rgba(255,255,255,0.10)]" : ""
      }`}>
      <nav
        className="container flex h-16 items-center justify-between gap-4"
        aria-label="Navegação principal">
        <Link href="/" className="flex shrink-0 items-center" aria-label="EquipaPRO — início">
          <img
            src={ASSETS.logoOrange}
            alt="EquipaPRO — Portas e Controle de Acesso"
            width={383}
            height={64}
            className="h-7 w-auto md:h-8"
          />
        </Link>

        <ul className="hidden items-center gap-7 md:flex">
          {NAV_LINKS.map((link) => {
            const active =
              link.href === "/" ? location === "/" : location.startsWith(link.href);
            return (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className={`text-sm font-medium transition-colors hover:text-warmgold ${
                    active ? "text-warmgold" : "text-white/75"
                  }`}>
                  {link.label}
                </Link>
              </li>
            );
          })}
          {/* Link da Loja Tray — visível apenas quando tray_show_nav_link = 1 */}
          {trayShowNav && <li>
            {trayUrl ? (
              <a
                href={trayUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-white/75 transition-colors hover:text-warmgold">
                <ShoppingBag className="h-3.5 w-3.5" />
                {trayLabel}
              </a>
            ) : (
              <Link
                href="/loja"
                className={`inline-flex items-center gap-1 text-sm font-medium transition-colors hover:text-warmgold ${
                  location === "/loja" ? "text-warmgold" : "text-white/75"
                }`}>
                <ShoppingBag className="h-3.5 w-3.5" />
                {trayLabel}
              </Link>
            )}
          </li>}
        </ul>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => openWhatsApp({ message: "Olá! Gostaria de falar com a equipe da EquipaPRO.", channel: "comercial", origin: "navbar" })}
            className="btn-press hidden rounded-md bg-warmgold px-4 py-2 text-sm font-semibold text-white hover:bg-warmgold/90 sm:inline-flex">
            {CTA_TEXT}
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            aria-expanded={open}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-white md:hidden">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="border-t border-white/10 bg-charcoal md:hidden">
          <ul className="container flex flex-col py-2">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="block py-3 text-base font-medium text-white/80 hover:text-warmgold">
                  {link.label}
                </Link>
              </li>
            ))}
            {trayShowNav && (
              <li>
                {trayUrl ? (
                  <a
                    href={trayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 py-3 text-base font-medium text-white/80 hover:text-warmgold">
                    <ShoppingBag className="h-4 w-4" />
                    {trayLabel}
                  </a>
                ) : (
                  <Link
                    href="/loja"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 py-3 text-base font-medium text-white/80 hover:text-warmgold">
                    <ShoppingBag className="h-4 w-4" />
                    {trayLabel}
                  </Link>
                )}
              </li>
            )}
            <li className="py-3">
              <button
                type="button"
                onClick={() => { setOpen(false); openWhatsApp({ message: "Olá! Gostaria de falar com a equipe da EquipaPRO.", channel: "comercial", origin: "navbar-mobile" }); }}
                className="btn-press inline-flex w-full items-center justify-center rounded-md bg-warmgold px-4 py-3 text-sm font-semibold text-white">
                {CTA_TEXT}
              </button>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
